import { createContext, useContext, useId, type ReactNode } from "react";
import { motion } from "motion/react";
import { Icon } from "./Icon";
import { haptic } from "../telegram/haptics";
import { micro, pressSpring } from "../motion";
import { useT } from "../i18n/strings";
import type { FieldLabel } from "../i18n/fieldLabels";
import { pick } from "../i18n/labels";
import s from "./fields.module.css";

/* ----------------------------------------------------------------- field -- */

interface FieldProps {
  label: FieldLabel;
  children: ReactNode;
  error?: string;
  hint?: string;
  optional?: boolean;
  htmlFor?: string;
}

/** Lets the one input inside a Field pick up the label's id without prop threading. */
const FieldId = createContext<string | undefined>(undefined);

/** One label, in the language the reader switched on. */
export function Field({ label, children, error, hint, optional, htmlFor }: FieldProps) {
  const t = useT();
  const autoId = useId();
  const id = htmlFor ?? autoId;
  return (
    <div className={s.field}>
      <div className={s.labelRow}>
        <label className={s.labelFa} htmlFor={id}>
          {pick(label)}
        </label>
        {optional && <span className={s.optional}>{t.form.optional}</span>}
      </div>
      <FieldId.Provider value={id}>{children}</FieldId.Provider>
      {error ? (
        <motion.p
          className={s.error}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={micro}
        >
          <Icon name="alert" size={16} />
          {error}
        </motion.p>
      ) : (
        hint && <p className={s.hint}>{hint}</p>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- inputs -- */

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  id?: string;
  /** Titles keep their original script, so the box must not force a direction. */
  auto?: boolean;
}

export function TextInput({ value, onChange, placeholder, invalid, id, auto }: TextInputProps) {
  const fieldId = useContext(FieldId);
  return (
    <input
      id={id ?? fieldId}
      type="text"
      className={[s.control, invalid && s.invalid].filter(Boolean).join(" ")}
      value={value}
      dir={auto ? "auto" : undefined}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function TextArea({
  value,
  onChange,
  placeholder,
  id,
  rows,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  rows?: number;
}) {
  const fieldId = useContext(FieldId);
  return (
    <textarea
      id={id ?? fieldId}
      rows={rows}
      dir="auto"
      className={`${s.control} ${s.textarea}`}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

interface NumberInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  placeholder?: string;
  invalid?: boolean;
  id?: string;
  step?: string;
  min?: number;
}

/** Empty means "not recorded", which is a different thing from zero. */
export function NumberInput({
  value,
  onChange,
  placeholder,
  invalid,
  id,
  step = "any",
  min,
}: NumberInputProps) {
  const fieldId = useContext(FieldId);
  return (
    <input
      id={id ?? fieldId}
      type="number"
      inputMode="decimal"
      step={step}
      min={min}
      className={[s.control, s.numeric, invalid && s.invalid].filter(Boolean).join(" ")}
      value={value === null || value === undefined || !Number.isFinite(value) ? "" : value}
      placeholder={placeholder}
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === "") {
          onChange(null);
          return;
        }
        const next = Number(raw);
        // Intermediate keystrokes (".", "-", "e") yield NaN; keep the field
        // empty rather than writing NaN into state (crashes some WebViews).
        onChange(Number.isFinite(next) ? next : null);
      }}
    />
  );
}

/* ------------------------------------------------------------- segmented -- */

interface SegmentedProps<T extends string> {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
  name: string;
}

export function Segmented<T extends string>({ value, options, onChange, name }: SegmentedProps<T>) {
  return (
    <div className={s.segmented} role="radiogroup">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            className={[s.segment, active && s.segmentActive].filter(Boolean).join(" ")}
            onClick={() => {
              haptic.select();
              onChange(option.value);
            }}
          >
            {active && (
              <motion.span
                layoutId={`segment-${name}`}
                className={s.segmentPill}
                transition={pressSpring}
              />
            )}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------- checkbox -- */

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}

/**
 * A drawn box over a real one: the native control keeps the keyboard and the
 * accessibility tree, the visible square takes the app's tokens. The tick is
 * spring-scaled in rather than appearing, which is the only motion a checkbox
 * gets anywhere in the app.
 */
export function Checkbox({ checked, onChange, children }: CheckboxProps) {
  return (
    <label className={s.checkRow}>
      <input
        type="checkbox"
        className={s.checkInput}
        checked={checked}
        onChange={(event) => {
          haptic.select();
          onChange(event.target.checked);
        }}
      />
      <span className={s.checkBox}>
        <motion.span
          initial={false}
          animate={{ scale: checked ? 1 : 0.4, opacity: checked ? 1 : 0 }}
          transition={pressSpring}
          style={{ display: "grid" }}
        >
          <Icon name="check" size={16} />
        </motion.span>
      </span>
      <span>{children}</span>
    </label>
  );
}

/* ------------------------------------------------------ three-cell group -- */

interface TripleProps {
  values: [number | null, number | null, number | null];
  onChange: (index: 0 | 1 | 2, value: number | null) => void;
  labels: [string, string, string];
  invalid?: boolean;
}

/** Width / height / depth, in centimetres. Depth is legitimately empty for a flat work. */
export function TripleNumber({ values, onChange, labels, invalid }: TripleProps) {
  const base = useId();
  return (
    <div className={s.triple}>
      {values.map((value, index) => (
        <div key={labels[index]} className={s.tripleCell}>
          <NumberInput
            id={`${base}-${index}`}
            value={value}
            min={0}
            invalid={invalid}
            onChange={(next) => onChange(index as 0 | 1 | 2, next)}
          />
          <span className={s.tripleLabel}>{labels[index]}</span>
        </div>
      ))}
    </div>
  );
}

export { s as fieldStyles };
